import { useCallback, useEffect, useState } from "react";

export type ReminderFrequency = "daily" | "weekly";

export interface WebReminderSettings {
  enabled: boolean;
  frequency: ReminderFrequency;
  time: string;
  nextAt: string | null;
}

const STORAGE_KEY = "wordinarius_echo.web.reminders.v1";
const DEFAULT_SETTINGS: WebReminderSettings = {
  enabled: false,
  frequency: "daily",
  time: "19:00",
  nextAt: null,
};

function validTime(value: string): boolean {
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
}

export function nextReminderAt(now: Date, frequency: ReminderFrequency, time: string): Date {
  const [hours, minutes] = (validTime(time) ? time : "19:00").split(":").map(Number);
  const next = new Date(now);
  next.setHours(hours, minutes, 0, 0);
  if (next.getTime() <= now.getTime()) next.setDate(next.getDate() + (frequency === "weekly" ? 7 : 1));
  return next;
}

export function applyReminderSettingsPatch(
  current: WebReminderSettings,
  patch: Partial<Pick<WebReminderSettings, "frequency" | "time">>,
  now = new Date(),
): WebReminderSettings {
  const next = { ...current, ...patch };
  return {
    ...next,
    nextAt: next.enabled ? nextReminderAt(now, next.frequency, next.time).toISOString() : null,
  };
}

function readSettings(): WebReminderSettings {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null") as Partial<WebReminderSettings> | null;
    if (!parsed) return DEFAULT_SETTINGS;
    return {
      enabled: parsed.enabled === true,
      frequency: parsed.frequency === "weekly" ? "weekly" : "daily",
      time: typeof parsed.time === "string" && validTime(parsed.time) ? parsed.time : "19:00",
      nextAt: typeof parsed.nextAt === "string" ? parsed.nextAt : null,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function useWebReminders(copy: { title: string; body: string }) {
  const [settings, setSettings] = useState<WebReminderSettings>(readSettings);
  const supported = typeof Notification !== "undefined" && "serviceWorker" in navigator;
  const permission = supported ? Notification.permission : "default";

  const persist = useCallback((resolve: WebReminderSettings | ((current: WebReminderSettings) => WebReminderSettings)) => {
    setSettings((current) => {
      const next = typeof resolve === "function" ? resolve(current) : resolve;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Reminder settings remain effective for the current session.
      }
      return next;
    });
  }, []);

  const enable = useCallback(async () => {
    if (!supported) return false;
    const result = await Notification.requestPermission();
    if (result !== "granted") return false;
    persist((current) => ({
      ...current,
      enabled: true,
      nextAt: nextReminderAt(new Date(), current.frequency, current.time).toISOString(),
    }));
    return true;
  }, [persist, supported]);

  const disable = useCallback(() => persist((current) => ({ ...current, enabled: false, nextAt: null })), [persist]);
  const update = useCallback((patch: Partial<Pick<WebReminderSettings, "frequency" | "time">>) => {
    persist((current) => applyReminderSettingsPatch(current, patch));
  }, [persist]);

  useEffect(() => {
    if (!supported || !settings.enabled || permission !== "granted" || !settings.nextAt) return;
    const check = async () => {
      if (Date.parse(settings.nextAt || "") > Date.now()) return;
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(copy.title, {
        body: copy.body,
        icon: "/assets/wordinarius-echo-product-logo-web.png",
        tag: "wordinarius-learning-reminder",
        data: { url: "/app/" },
      });
      persist({
        ...settings,
        nextAt: nextReminderAt(new Date(), settings.frequency, settings.time).toISOString(),
      });
    };
    void check();
    const timer = window.setInterval(() => void check(), 60_000);
    return () => window.clearInterval(timer);
  }, [copy.body, copy.title, permission, persist, settings, supported]);

  return { disable, enable, permission, settings, supported, update };
}
